import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMemo, useEffect } from 'react';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import { getTenderPackageDetailsQuery } from '@/app/hooks/queries/useTenderPackages';
import {
  Breadcrumb,
  BreadcrumbMenu,
  BreadcrumbMenuItem,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/app/ui/components/breadcrumb';
import {
  Header,
  HeaderSidechatButton,
  HeaderBackButton,
  HeaderSidebarTrigger,
} from '@/app/ui/components/header';
import {
  EvaluationTypeLabelsEnum,
  ProxyDocumentTypes,
  queryKeys,
  REFETCH_SUMMARIZATION_INTERVAL,
  SummarizationNamespaces,
} from '@/app/utils/constants';
import { DocumentAlerts } from '@/app/components/shared/EntityDocuments/DocumentAlerts';
import { FloatingAlert } from '@/app/components/shared/FloatingAlert';
import { AuctionIcon, Alert01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useTranslations } from 'use-intl';
import { MainContainer, MainHeaderBackButton, MainScrollArea } from '@/app/ui/components/main';
import { H1, H2, H3 } from '@/app/ui/components/heading';
import { Spinner } from '@/app/ui/components/spinner';
import { getBidDetailsQuery } from '@/app/hooks/queries/useBids';
import { Card } from '@/app/ui/components/card';
import {
  useCvAssessmentDetailsQuery,
  useCvAssessmentAnalysisMutation,
} from '@/app/hooks/queries/useCvAssessment';
import useDocumentsIndexingStatus from '@/app/hooks/useDocumentsIndexingStatus';
import { Alert, AlertDescription } from '@/app/ui/components/alert';
import { Frame, FramePanel } from '@/app/ui/components/frame';
import { CircularProgress } from '@/app/ui/components/circular-progress';
import { getBidAssessmentColor } from '@/app/components/features/bids/utils';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/tender-packages/$tenderPackageId/bids/$bidId/_layout/cv-analysis/',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations('pages.bids');
  const tCv = useTranslations('components.bidCvs');
  const tGeneral = useTranslations('general');
  const queryClient = useQueryClient();
  const { bidId, tenderPackageId, projectId } = Route.useParams();
  const { data: bid } = useSuspenseQuery(getBidDetailsQuery(bidId));
  const { data: evaluation } = useSuspenseQuery(getTenderPackageDetailsQuery(tenderPackageId));
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));
  const navigate = useNavigate();

  const { data, isLoading } = useCvAssessmentDetailsQuery({
    bidId,
    refetchInterval: REFETCH_SUMMARIZATION_INTERVAL * 1000,
  });

  const { mutateAsync: triggerAnalysis, isPending: isMutationPending } =
    useCvAssessmentAnalysisMutation(bidId);

  const isProcessing = data?.status === 10;
  const versionStatus = data?.versionStatus;

  const { areDocumentsIndexed } = useDocumentsIndexingStatus({
    documentType: ProxyDocumentTypes.BID,
    id: bidId,
  });

  useEffect(() => {
    if (versionStatus === 'stale') {
      void queryClient.invalidateQueries({
        queryKey: [queryKeys.INDEXING_STATUS, ProxyDocumentTypes.BID, bidId],
      });
    }
  }, [versionStatus, queryClient, bidId]);

  const { categorized, other } = useMemo(() => {
    if (!data) return { categorized: [], other: [] };

    const categorized = data.categories.filter(
      (c) => c.tenderCriteriaPosition.id !== 'uncategorized',
    );

    const uncategorized = data.categories.find(
      (c) => c.tenderCriteriaPosition.id === 'uncategorized',
    );
    const other = uncategorized ? uncategorized.positions : [];
    return { categorized, other };
  }, [data]);

  return (
    <>
      <Header>
        <HeaderBackButton />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbMenu>
              <BreadcrumbMenuItem
                to="/project/$projectId/tender-packages/$tenderPackageId"
                params={{ projectId, tenderPackageId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.TENDER_PACKAGE} />
                {evaluation.name}
              </BreadcrumbMenuItem>
              <BreadcrumbMenuItem to="/project/$projectId" params={{ projectId }}>
                <ProjectTypeIcon type={project.type} />
                {project.name}
              </BreadcrumbMenuItem>
            </BreadcrumbMenu>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/tender-packages/$tenderPackageId/bids"
                params={{ projectId, tenderPackageId }}
              >
                <HugeiconsIcon icon={AuctionIcon} />
                {t('title')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/tender-packages/$tenderPackageId/bids/$bidId"
                params={{ projectId, tenderPackageId, bidId }}
              >
                <span className="line-clamp-1">{bid.bidAdditionalName || bid.companyName}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{tCv('cards.cvAnalysis.title')}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderSidechatButton />
      </Header>
      <MainScrollArea>
        <DocumentAlerts
          resourceId={bidId}
          summaryType={SummarizationNamespaces.BID}
          onGoToFiles={() =>
            navigate({
              to: '/project/$projectId/tender-packages/$tenderPackageId/bids/$bidId/files',
              params: { projectId, tenderPackageId, bidId },
            })
          }
        />
        {isLoading ? (
          <Spinner kind="inset" />
        ) : (
          <MainContainer>
            <H1 className="mb-6">
              <MainHeaderBackButton />
              {tCv('title')}
            </H1>
            <Alert variant="destructive" className="mb-5">
              <HugeiconsIcon icon={Alert01Icon} />
              <AlertDescription>{tCv('alerts.focusOnCv')}</AlertDescription>
            </Alert>
            {data?.summary && (
              <Card className="mb-6">
                <div className="flex items-start gap-6">
                  <div className="flex-1">
                    <H2 className="text-base mb-3">{tCv('subtitle')}</H2>
                    <p className="text-sm text-muted leading-6">{data.summary}</p>
                  </div>
                  {data.totalScore !== undefined && data.totalScore !== null && (
                    <CircularProgress
                      max={10}
                      getValueText={(value) => value.toFixed(1)}
                      value={data.totalScore}
                      size={120}
                      label={tCv('cards.cvAnalysis.scoreLabel')}
                      color={getBidAssessmentColor(data.totalScore)}
                    />
                  )}
                </div>
              </Card>
            )}
            <div className="mb-4">
              <H2>{tCv('labels.positions')}</H2>
              <p className="text-sm text-muted">{tCv('labels.positionsDescription')}</p>
            </div>
            <div className="space-y-6">
              {categorized.map((category) => (
                <div key={category.tenderCriteriaPosition.id}>
                  <H3 className="mb-4">{category.tenderCriteriaPosition.positionTitle}</H3>
                  <Frame stackedPanels className="@3xl:grid-cols-2 @5xl:grid-cols-3">
                    {category.positions.map((position, index) => (
                      <BidCvScoreCard
                        key={position.id}
                        score={position.score ?? undefined}
                        title={position.positionName}
                        candidateName={position.candidateName}
                        id={position.id}
                        index={index + 1}
                      />
                    ))}
                  </Frame>
                </div>
              ))}
              {!!other.length && (
                <>
                  <div className="mt-6">
                    <H2>{tCv('labels.otherCvs')}</H2>
                    <p className="text-sm text-muted">{tCv('labels.otherCvsDescription')}</p>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    {other.map((position, index) => (
                      <Card
                        render={
                          <Link
                            to="/project/$projectId/tender-packages/$tenderPackageId/bids/$bidId/cv-analysis/other/$positionId"
                            params={{ projectId, tenderPackageId, bidId, positionId: position.id }}
                          />
                        }
                        className="cursor-pointer hover:opacity-90 transition-opacity"
                      >
                        <div className="p-4 pt-3">
                          <div className="pt-1 text-[15px] flex gap-1">
                            <span className="text-muted font-normal">
                              {tCv('labels.cvIndex', { index: index + 1 })}
                            </span>
                            <span className="font-semibold">
                              {position.candidateName ?? tCv('labels.anonymous')}
                            </span>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </>
              )}
            </div>
          </MainContainer>
        )}
      </MainScrollArea>
      {versionStatus === 'stale' && !isProcessing && (
        <FloatingAlert
          type="warning"
          title={tGeneral('staleAnalysisAlert.title')}
          description={tGeneral('staleAnalysisAlert.description')}
          actionLabel={tGeneral('staleAnalysisAlert.action')}
          onActionClick={async () => {
            await triggerAnalysis();
          }}
          actionPending={isMutationPending}
          actionDisabled={!areDocumentsIndexed}
        />
      )}
    </>
  );
}

function BidCvScoreCard({
  title,
  score,
  candidateName,
  index,
  id,
}: {
  title?: string;
  score?: number;
  index: number;
  id: string;
  candidateName?: string;
}) {
  const t = useTranslations('components.bidCvs');
  const { bidId, tenderPackageId, projectId } = Route.useParams();

  const hasCv = score !== undefined && score !== null;

  return (
    <FramePanel
      render={
        <Link
          to="/project/$projectId/tender-packages/$tenderPackageId/bids/$bidId/cv-analysis/$positionId"
          params={{ projectId, tenderPackageId, bidId, positionId: id }}
        />
      }
      className="min-h-60 flex flex-col hover:bg-surface-1 transition"
    >
      <div>
        <p className="text-lg -mt-1 font-medium">{title}</p>
        <div className="text-muted text-sm">
          {hasCv ? (
            <div className="flex gap-1">
              <span className="text-muted-foreground">{t('labels.cvIndex', { index })}</span>
              <span className="text-foreground font-medium">
                {candidateName?.length ? candidateName : t('labels.anonymous')}
              </span>
            </div>
          ) : (
            <span>{t('cards.categorizedCV.noMatch')}</span>
          )}
        </div>
      </div>
      <div className="flex justif y-center items-center cursor-pointer h-full min-h-64 min-w-64">
        {hasCv ? (
          <CircularProgress
            value={score}
            max={10}
            getValueText={(val) => val.toFixed(1)}
            size={160}
            label={t('cards.categorizedCV.scoreLabel')}
            className="my-8 md:my-16 mx-auto"
            color={getBidAssessmentColor(score)}
          />
        ) : (
          <p className="text-sm font-semibold group-hover:underline">
            {t('cards.categorizedCV.uploadCv')}
          </p>
        )}
      </div>
    </FramePanel>
  );
}
